import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Share, Instagram } from 'lucide-react';
import ScrollToTop from '../components/ScrollToTop';
import WhatsAppButton from '../components/WhatsAppButton';

const Media: React.FC = () => {
  const { t, language } = useLanguage();

  const instagramPosts = [
    {
      id: 'ig-001',
      image: '/resimler/media1.png',
      caption: language === 'tr' 
        ? 'Yeni yangın güvenlik sistemimiz ile denizcilik sektöründe güvenlik standartlarını yükseltiyoruz! 🔥⚓ #AymerSafety #DenizcilikGüvenliği'
        : 'Raising safety standards in the maritime industry with our new fire safety system! 🔥⚓ #AymerSafety #MaritimeSafety',
      likes: 127,
      comments: 23,
      date: '2024-01-20'
    },
    {
      id: 'ig-002',
      image: '/resimler/logo.jpeg ',
      caption: language === 'tr'
        ? 'Profesyonel ekibimiz ile gemi yangın söndürme sistemi kurulumu tamamlandı! ✅🚢 #Profesyonel #Kalite'
        : 'Ship fire extinguishing system installation completed with our professional team! ✅🚢 #Professional #Quality',
      likes: 89,
      comments: 15,
      date: '2024-01-18'
    },
    {
      id: 'ig-003',
      image: '/resimler/yangin1.jpg',
      caption: language === 'tr'
        ? 'Can kurtarma ekipmanları kontrolü ve bakımı tamamlandı. Güvenliğiniz bizim önceliğimiz! 🛟⚡ #CanKurtarma #Güvenlik'
        : 'Life-saving equipment inspection and maintenance completed. Your safety is our priority! 🛟⚡ #LifeSaving #Safety',
      likes: 156,
      comments: 31,
      date: '2024-01-15'
    },
    {
      id: 'ig-004',
      image: '/resimler/html.jpg',
      caption: language === 'tr'
        ? 'Yeni ürün kataloğumuz hazır! Denizcilik güvenlik çözümleri için bizimle iletişime geçin 📋🔧 #YeniKatalog #Ürünler'
        : 'Our new product catalog is ready! Contact us for maritime safety solutions 📋🔧 #NewCatalog #Products',
      likes: 203,
      comments: 42,
      date: '2024-01-12'
    },
    {
      id: 'ig-005',
      image: '/resimler/katalog1.png',
      caption: language === 'tr'
        ? 'Üretim tesisimizde kalite kontrol süreçlerimiz devam ediyor. CE standartlarında üretim! 🏭✅ #Kalite #Üretim'
        : 'Quality control processes continue in our production facility. Production to CE standards! 🏭✅ #Quality #Production',
      likes: 94,
      comments: 18,
      date: '2024-01-10'
    },
    {
      id: 'ig-006',
      image: '/resimler/unnamed.jpg',
      caption: language === 'tr'
        ? 'Lüks yat projemizde yangın güvenlik sistemleri kurulumu başarıyla tamamlandı! 🛥️🔥 #LüksYat #Proje'
        : 'Fire safety systems installation successfully completed in our luxury yacht project! 🛥️🔥 #LuxuryYacht #Project',
      likes: 178,
      comments: 27,
      date: '2024-01-08'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-amber-400">
            {t('mediaTitle')}
          </h1>
          <div className="w-24 h-1 bg-amber-400 mx-auto mb-6"></div>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            {t('mediaSubtitle')}
          </p>
        </motion.div>

        {/* Instagram Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-16"
        >
          <div className="flex items-center justify-center mb-8">
            <Instagram className="h-8 w-8 text-pink-500 mr-3" />
            <h2 className="text-2xl font-bold text-white">
              @aymersafety
            </h2>
          </div>
          
          {/* Instagram Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {instagramPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-slate-800 rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-slate-700"
              >
                {/* Instagram Post Header */}
                <div className="flex items-center p-4 border-b border-slate-700">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
                    <Instagram className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">aymersafety</p>
                    <p className="text-slate-400 text-xs">
                      {new Date(post.date).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                </div>

                {/* Instagram Post Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt="Instagram Post"
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>

                {/* Instagram Post Actions */}
                <div className="p-4">
                  <div className="flex items-center space-x-4 mb-3">
                    <button className="flex items-center space-x-1 text-slate-400 hover:text-red-500 transition-colors duration-200">
                      <Heart className="h-5 w-5" />
                      <span className="text-sm">{post.likes}</span>
                    </button>
                    <button className="flex items-center space-x-1 text-slate-400 hover:text-blue-500 transition-colors duration-200">
                      <MessageCircle className="h-5 w-5" />
                      <span className="text-sm">{post.comments}</span>
                    </button>
                    <button className="text-slate-400 hover:text-amber-400 transition-colors duration-200">
                      <Share className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Instagram Post Caption */}
                  <p className="text-slate-300 text-sm leading-relaxed">
                    <span className="font-semibold text-white">aymersafety</span> {post.caption}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Instagram Follow Button */}
          <div className="text-center">
            <motion.a
              href="https://www.instagram.com/aymersafety/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200"
            >
              <Instagram className="h-5 w-5" />
              <span>{language === 'tr' ? 'Instagram\'da Takip Et' : 'Follow on Instagram'}</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
      
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default Media;